// FinancePositionBanner — the purchase-order position header. Presents four
// figures (Approved · Awaiting review · Remaining · Approved limit) each in
// ex-GST dollars, anchored to a single horizontal stacked bar whose three
// segments are proportional to the PO limit: Approved (emerald-700), Awaiting
// review (emerald-500), Remaining (slate-400). Pure/presentational — the
// derived `position` rollup is passed in by FinanceTab, which sources it from
// the GET purchase-order response (never a mutation response). The bar is
// exposed to assistive tech as a single role="img" summarising all four
// figures, so the colour is redundant reinforcement rather than the only
// channel.

import { formatAud, type PoPosition } from "../../../utils/financeCalc";
import { gstSuffix } from "../../../utils/gstSuffix";

interface FinancePositionBannerProps {
  position: PoPosition;
  /** Stored GST intent on the selected purchase order — drives the ex-GST /
   *  no-GST / not-recorded meta under each figure. */
  gstApplies?: boolean;
}

// Percentage of the limit a segment occupies, clamped to [0, 100] and guarded
// against a zero limit so an unfunded PO renders an empty track rather than a
// NaN width. A negative remaining (overdrawn) floors at 0.
function segmentPct(value: number, limit: number): number {
  if (limit <= 0) return 0;
  const pct = (Math.max(0, value) / limit) * 100;
  return Math.min(100, pct);
}

interface Figure {
  key: string;
  label: string;
  value: number;
  /** Legend swatch class matching this figure's bar segment; omitted for the
   *  total (limit), which is not a segment. */
  swatch?: string;
}

export default function FinancePositionBanner({
  position,
  gstApplies,
}: FinancePositionBannerProps) {
  const { limit, approved, inReview, remaining } = position;
  const suffix = gstSuffix(gstApplies);

  const approvedPct = segmentPct(approved, limit);
  const inReviewPct = segmentPct(inReview, limit);
  const remainingPct = segmentPct(remaining, limit);

  const figures: Figure[] = [
    {
      key: "approved",
      label: "Approved",
      value: approved,
      swatch: "bg-emerald-700 dark:bg-emerald-600",
    },
    {
      key: "in-review",
      label: "Awaiting review",
      value: inReview,
      swatch: "bg-emerald-500 dark:bg-emerald-400",
    },
    {
      key: "remaining",
      label: "Remaining",
      value: remaining,
      swatch: "bg-slate-400 dark:bg-slate-500",
    },
    { key: "limit", label: "Approved limit", value: limit },
  ];

  const barLabel = `Purchase order position: approved ${formatAud(
    approved,
  )}, awaiting review ${formatAud(inReview)}, remaining ${formatAud(
    remaining,
  )} of an approved limit of ${formatAud(limit)}, all ${suffix}.`;

  return (
    <section
      aria-label="Purchase order position"
      className="rounded-lg border border-slate-200/60 bg-white p-5 shadow-soft-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-soft-sm-dark"
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {figures.map((figure) => (
          <div key={figure.key}>
            <div className="flex items-center gap-1.5">
              {figure.swatch && (
                <span
                  aria-hidden="true"
                  className={`h-2.5 w-2.5 shrink-0 rounded-sm ${figure.swatch}`}
                />
              )}
              <span className="text-overline uppercase text-slate-500 dark:text-slate-400">
                {figure.label}
              </span>
            </div>
            <div className="mt-1 text-h3 tabular-nums text-slate-900 dark:text-slate-100">
              {formatAud(figure.value)}
            </div>
            <div className="text-caption text-slate-500 dark:text-slate-400">
              {suffix}
            </div>
          </div>
        ))}
      </div>

      <div
        role="img"
        aria-label={barLabel}
        className="mt-4 flex h-3 w-full gap-px overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
      >
        <div
          data-testid="segment-approved"
          className="h-full bg-emerald-700 dark:bg-emerald-600"
          style={{ width: `${approvedPct}%` }}
        />
        <div
          data-testid="segment-in-review"
          className="h-full bg-emerald-500 dark:bg-emerald-400"
          style={{ width: `${inReviewPct}%` }}
        />
        <div
          data-testid="segment-remaining"
          className="h-full bg-slate-400 dark:bg-slate-500"
          style={{ width: `${remainingPct}%` }}
        />
      </div>
    </section>
  );
}
